import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/store/Logo";
import { Reveal } from "@/components/store/Reveal";
import { AD_SCRIPTS, GOOGLE_ADS, TIKTOK_CAPTIONS, REPLY_SCRIPTS } from "@/lib/data";
import { fetchSettings, saveSettings, fetchOrders } from "@/lib/api";
import { Copy, Rocket, Save, Eye, EyeOff, Package, Megaphone, Search, Wallet, Truck, RefreshCw, Lock, Download, Clapperboard, Zap, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-xl border border-white/15 bg-[#0b0f18] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors focus:border-cyan-400/60";

function Section({ icon: Icon, title, sub, children, testId }) {
  return (
    <Reveal>
      <section data-testid={testId} className="rounded-3xl border border-white/10 bg-[#0e131f] p-7 sm:p-9">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-400/10">
            <Icon className="h-5 w-5 text-cyan-300" />
          </div>
          <div>
            <h2 className="font-display text-xl font-bold text-white">{title}</h2>
            <p className="text-xs text-slate-500">{sub}</p>
          </div>
        </div>
        <div className="mt-6">{children}</div>
      </section>
    </Reveal>
  );
}

function CopyButton({ text, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }}
      className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
    >
      <Copy className="h-3 w-3" /> Copy
    </button>
  );
}

const CREATIVE_GROUPS = [
  {
    label: "Video Ads — Reels / Stories / TikTok (9:16 vertical, ~13s)",
    items: [
      { href: "/ads/vid-racing-mind-vertical.mp4", poster: "/ads/poster-racing-mind-vertical.jpg", name: "Racing Mind", spec: "1080×1920 · your #1 cold hook", video: true },
      { href: "/ads/vid-three-am-vertical.mp4", poster: "/ads/poster-three-am-vertical.jpg", name: "3AM Club", spec: "1080×1920 · night-owl scrollers", video: true },
      { href: "/ads/vid-science-vertical.mp4", poster: "/ads/poster-science-vertical.jpg", name: "The Science", spec: "1080×1920 · melatonin signal", video: true },
      { href: "/ads/vid-seven-nights-vertical.mp4", poster: "/ads/poster-seven-nights-vertical.jpg", name: "7 Nights Countdown", spec: "1080×1920 · product tour", video: true },
      { href: "/ads/vid-reviews-vertical.mp4", poster: "/ads/poster-reviews-vertical.jpg", name: "Social Proof", spec: "1080×1920 · retargeting", video: true },
    ],
  },
  {
    label: "Video Ad — Instagram Feed (1:1 square)",
    items: [
      { href: "/ads/vid-racing-mind-square.mp4", poster: "/ads/poster-racing-mind-square.jpg", name: "Racing Mind (square)", spec: "1080×1080 · IG feed", video: true },
    ],
  },
  {
    label: "Image Ads — Instagram Feed (1:1)",
    items: [
      { href: "/ads/ig-square-brain.jpg", name: "Your Brain at 11PM", spec: "1080×1080 · relatable hook" },
      { href: "/ads/ig-square-3amclub.jpg", name: "The 3AM Club", spec: "1080×1080 · punchy clock gag" },
      { href: "/ads/ig-square-review.jpg", name: "Review Card", spec: "1080×1080 · social proof" },
      { href: "/ads/ad-sleep-square-main.jpg", name: "Fall Asleep Hook", spec: "1080×1080 · classic" },
      { href: "/ads/ad-sleep-square-3am.jpg", name: "Wide Awake at 3AM?", spec: "1080×1080 · classic" },
    ],
  },
  {
    label: "Image Ads — Stories (9:16)",
    items: [
      { href: "/ads/ig-story-swipe.jpg", name: "Tonight's Chapter", spec: "1080×1920 · story swipe-up" },
      { href: "/ads/ig-story-seven.jpg", name: "7 Nights List", spec: "1080×1920 · story product tour" },
    ],
  },
  {
    label: "Sneak Peeks — real pages from inside the guide",
    items: [
      { href: "/ads/ig-peek-map.jpg", name: "The 7-Night Map", spec: "real page · best teaser" },
      { href: "/ads/ig-peek-night2.jpg", name: "Night 2: Racing Mind", spec: "real page · star technique" },
      { href: "/ads/ig-peek-tracker.jpg", name: "30-Night Tracker", spec: "real page · habit hook" },
    ],
  },
  {
    label: "Google Display + Brand",
    items: [
      { href: "/ads/ad-sleep-landscape.jpg", name: "Landscape Display", spec: "1200×628 · Google Display" },
      { href: "/sleep-reset-cover.png", name: "Guide Cover", spec: "1275×1650 · product shot" },
      { href: "/sleep-reset-og.png", name: "Social Card", spec: "1200×630 · link previews" },
      { href: "/astra-logo.png", name: "ASTRA Logo", spec: "1200×1200 · Google Ads asset" },
    ],
  },
];

const STATUS_STYLES = {
  PENDING: "border-slate-400/30 text-slate-400",
  QUEUED: "border-amber-400/40 text-amber-300",
  SUBMITTING: "border-cyan-400/40 text-cyan-300",
  SUBMITTED: "border-emerald-400/40 text-emerald-300",
  FAILED: "border-rose-400/40 text-rose-300",
  SHIPPED: "border-emerald-400/40 text-emerald-300",
  DELIVERED: "border-emerald-400/40 text-emerald-300",
};

export default function LaunchKit() {
  const [adminKey, setAdminKey] = useState(localStorage.getItem("astra_admin_key") || "");
  const [showKey, setShowKey] = useState(false);
  const [settings, setSettings] = useState({ meta_pixel_id: "", ga_id: "", stripe_pk: "", cj_api_key: "", cj_product_vid: "", cj_sandbox: true });
  const [cjConfigured, setCjConfigured] = useState(false);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [stripeSetupUrl, setStripeSetupUrl] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const tryUnlock = async (key) => {
    if (!key || unlocking) return;
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/stats`, { headers: { "X-Admin-Key": key } });
      if (!res.ok) throw new Error("bad key");
      setAdminKey(key);
      localStorage.setItem("astra_admin_key", key);
      setUnlocked(true);
      loadOrders();
      loadStripeSetup();
    } catch {
      toast.error("Wrong key — check your Launch Kit key.");
    } finally {
      setUnlocking(false);
    }
  };

  const loadStripeSetup = () => {
    fetch(`${API}/stripe/setup`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStripeSetupUrl(d.onboarding_url))
      .catch(() => {});
  };

  useEffect(() => {
    fetchSettings()
      .then((d) => {
        setSettings((s) => ({ ...s, meta_pixel_id: d.meta_pixel_id || "", ga_id: d.ga_id || "", stripe_pk: d.stripe_pk || "", cj_sandbox: d.cj_sandbox !== false }));
        setCjConfigured(!!d.cj_configured);
      })
      .catch(() => {});
  }, []);

  const loadOrders = () => {
    fetchOrders(adminKey).then(setOrders).catch(() => toast.error("Wrong admin key — check your Launch Kit key."));
    fetch(`${API}/stats`, { headers: { "X-Admin-Key": adminKey } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setStats(d))
      .catch(() => {});
  };

  useEffect(() => {
    if (adminKey) tryUnlock(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!unlocked) {
    return (
      <div data-testid="launchkit-lock-screen" className="relative flex min-h-screen flex-col items-center justify-center bg-[#07090e] px-4">
        <div className="absolute left-6 top-6"><Link to="/"><Logo /></Link></div>
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0e131f] p-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-cyan-400/30 bg-cyan-400/10">
            <Lock className="h-6 w-6 text-cyan-300" />
          </div>
          <h1 className="font-display mt-5 text-2xl font-bold text-white">Owner access only</h1>
          <p className="mt-2 text-sm text-slate-400">Enter your Launch Kit key to open the dashboard.</p>
          <input
            data-testid="lock-key-input"
            type="password"
            placeholder="Launch Kit key"
            onKeyDown={(e) => e.key === "Enter" && tryUnlock(e.target.value)}
            onChange={(e) => setAdminKey(e.target.value)}
            className="mt-6 w-full rounded-xl border border-white/15 bg-[#0b0f18] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none transition-colors focus:border-cyan-400/60"
          />
          <button
            data-testid="lock-unlock-button"
            onClick={() => tryUnlock(adminKey)}
            disabled={unlocking || !adminKey}
            className="mt-3 w-full rounded-full bg-cyan-400 py-3.5 text-sm font-bold text-[#07090e] transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {unlocking ? "Checking…" : "Unlock Dashboard"}
          </button>
          <Link to="/" data-testid="lock-back-home-link" className="mt-5 inline-block text-xs text-slate-500 transition-colors hover:text-slate-300">
            ← Back to the store
          </Link>
        </div>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const d = await saveSettings(adminKey, settings);
      localStorage.setItem("astra_admin_key", adminKey);
      setCjConfigured(!!d.cj_configured);
      toast.success("Saved. Tracking settings are live.");
    } catch {
      toast.error("Save failed — check your admin key.");
    } finally {
      setSaving(false);
    }
  };

  const refundOrder = async (orderNumber) => {
    if (!window.confirm(`Refund ${orderNumber} in full via Stripe? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/orders/${orderNumber}/refund`, { method: "POST", headers: { "X-Admin-Key": adminKey } });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Refund failed");
      toast.success(`${orderNumber} refunded — the money is on its way back to the buyer`);
      loadOrders();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="launch-kit-panel" className="min-h-screen bg-[#07090e]">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0e131f]/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="launch-kit-home-link"><Logo /></Link>
          <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-cyan-400">
            <Rocket className="h-4 w-4" /> Ad Launch Kit
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-4 py-12 sm:px-6">
        <Reveal>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Plug in. <span className="text-gradient">Launch.</span> Profit.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-400">
            Everything your store needs to run itself: tracking pixels, ad scripts, and instant hands-free delivery. No code, no agencies, no inventory.
          </p>
        </Reveal>

        <Reveal>
          <div data-testid="sales-dashboard" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[
              { label: "Revenue", value: stats ? `$${stats.revenue.toFixed(2)}` : "—", sub: stats ? (stats.refund_total ? `$${stats.refund_total.toFixed(2)} refunded` : "gross paid sales") : "enter admin key", testId: "stat-revenue" },
              { label: "Paid Orders", value: stats ? String(stats.paid_count) : "—", sub: stats ? `${stats.total_orders} total checkouts` : "enter admin key", testId: "stat-orders" },
              { label: "Checkout Conversion", value: stats ? `${stats.conversion}%` : "—", sub: "paid vs started", testId: "stat-conversion" },
              { label: "Rescued by Follow-ups", value: stats ? `$${stats.rescue_recovered_revenue.toFixed(2)}` : "—", sub: stats ? `${stats.rescue_recovered_count} recovered order(s)` : "cart rescue emails", testId: "stat-rescued" },
            ].map((c) => (
              <div key={c.label} data-testid={c.testId} className="rounded-3xl border border-white/10 bg-[#0e131f] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">{c.label}</p>
                <p className="font-display mt-2 text-2xl font-extrabold text-white sm:text-3xl">{c.value}</p>
                <p className="mt-1 text-[11px] text-slate-500">{c.sub}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Section icon={Zap} title="Delivery — 100% Automatic" sub="Digital product: no supplier, no shipping, no manual work" testId="launch-kit-fulfillment">
          <div className="mb-5 flex items-center gap-2.5 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4 text-sm font-semibold text-emerald-300">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            Live — every paid order delivers the PDF instantly (download page + email)
          </div>
          <ol className="mb-5 list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-slate-400">
            <li>Customer pays on Stripe's secure checkout — $19.95</li>
            <li>The store generates a private download link for their order</li>
            <li>They get the guide on the confirmation page AND by email, seconds later</li>
            <li>You keep ~$19.35 per sale after card fees — zero product cost</li>
          </ol>
          <div className="rounded-2xl border border-white/10 bg-[#131a29] p-5 text-sm leading-relaxed text-slate-400">
            Refunds: use the Refund button on any paid order below — money returns to the customer's card in 5-10 days.
            The 30-night guarantee is your conversion weapon; refunds on digital products are rare and cheap.
          </div>
          <button
            data-testid="owner-download-button"
            onClick={async () => {
              try {
                const res = await fetch(`${API}/product/download`, { headers: { "X-Admin-Key": adminKey } });
                if (!res.ok) throw new Error();
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = "The-7-Night-Sleep-Reset.pdf";
                a.click();
                URL.revokeObjectURL(a.href);
                toast.success("Downloading your copy of the guide");
              } catch {
                toast.error("Download failed — check your admin key");
              }
            }}
            className="mt-4 flex items-center gap-2 rounded-full border border-cyan-400/40 px-5 py-3 text-sm font-semibold text-cyan-300 transition-colors hover:bg-cyan-400/10"
          >
            <Download className="h-4 w-4" /> Download your own copy of the guide (owner)
          </button>
        </Section>

        <Section icon={Wallet} title="Plug & Play Integrations" sub="Your keys activate tracking across the whole storefront instantly" testId="launch-kit-integrations">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Admin Key (protects your settings)</label>
              <div className="relative">
                <input data-testid="admin-key-input" type={showKey ? "text" : "password"} className={inputCls} value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Default: astra-admin-2026" />
                <button onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                  {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Meta Pixel ID</label>
              <input data-testid="meta-pixel-input" className={inputCls} value={settings.meta_pixel_id} onChange={(e) => setSettings({ ...settings, meta_pixel_id: e.target.value })} placeholder="e.g. 1234567890123456" />
              <p className="mt-1.5 text-[11px] text-slate-500">Meta Events Manager → Data Sources → your Pixel ID</p>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Google Analytics / Ads Tag ID</label>
              <input data-testid="ga-id-input" className={inputCls} value={settings.ga_id} onChange={(e) => setSettings({ ...settings, ga_id: e.target.value })} placeholder="e.g. G-XXXXXXXXXX or AW-XXXXXXXXX" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Stripe Payouts</label>
              <div className="rounded-xl border border-emerald-400/25 bg-emerald-400/5 p-4">
                <p className="text-xs leading-relaxed text-slate-300">
                  Your Stripe account is <span className="font-bold text-emerald-300">connected in LIVE mode</span> — real cards, real payouts to your bank. Manage payments, refunds, and payout schedule in your dashboard.
                </p>
                {stripeSetupUrl ? (
                  <a
                    data-testid="stripe-setup-link"
                    href={stripeSetupUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-5 py-2.5 text-xs font-bold text-[#07090e] transition-transform hover:scale-105"
                  >
                    Open Stripe Dashboard →
                  </a>
                ) : (
                  <button data-testid="stripe-setup-load" onClick={loadStripeSetup} className="mt-3 rounded-full border border-emerald-400/40 px-5 py-2.5 text-xs font-bold text-emerald-300 transition-colors hover:bg-emerald-400/10">
                    Reveal my Stripe dashboard link
                  </button>
                )}
                <p className="mt-2.5 text-[11px] leading-relaxed text-slate-500">
                  Sales tax is calculated by Stripe at checkout. If you ever need to update your business address or bank details, that's in Dashboard → Settings.
                </p>
              </div>
            </div>
          </div>
          <button data-testid="save-meta-pixel-button" onClick={save} disabled={saving} className="mt-5 flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-[#07090e] transition-transform hover:scale-105 active:scale-95 disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save & Activate"}
          </button>
        </Section>

        <Section icon={Megaphone} title="5 Ready-to-Run Meta Ad Scripts" sub="Scroll-stopping hooks for the sleep market — copy, paste, launch" testId="launch-kit-meta-ads">
          <div className="space-y-4">
            {AD_SCRIPTS.map((ad, i) => (
              <div key={i} data-testid={`ad-script-${i}`} className="rounded-2xl border border-white/10 bg-[#131a29] p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-base font-bold text-white">"{ad.hook}"</p>
                  <CopyButton testId={`copy-ad-script-${i}`} text={`HOOK: ${ad.hook}\n\n${ad.body}\n\nCTA: ${ad.cta}`} />
                </div>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{ad.body}</p>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-cyan-400">CTA: {ad.cta}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-5 text-sm leading-relaxed text-slate-300">
            <p className="font-bold text-emerald-300">Cheap-but-converting Meta plan:</p>
            <p className="mt-1.5 text-slate-400">Start at $10/day with Advantage+ Shopping, broad targeting (18-45, US/UK/CA/AU), all 5 hooks as separate ads. Kill anything above $25 cost-per-purchase after 3 days, double budget on winners. Retarget video-viewers & site visitors with the reviews screenshot — that's your cheapest conversion.</p>
          </div>
        </Section>

        <Section icon={MessageSquare} title="TikTok Caption Bank + Reply Scripts" sub="30 ready-to-paste captions and what to say in your comments — copy, paste, post" testId="launch-kit-captions">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Captions (pair with the matching video)</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {TIKTOK_CAPTIONS.map((c, i) => (
              <div key={i} data-testid={`caption-${i}`} className="rounded-xl border border-white/10 bg-[#131a29] p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-400">{c.group}</p>
                  <CopyButton testId={`copy-caption-${i}`} text={c.text} />
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{c.text}</p>
              </div>
            ))}
          </div>
          <p className="mb-3 mt-8 text-xs font-semibold uppercase tracking-wider text-slate-400">Comment reply scripts</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {REPLY_SCRIPTS.map((r, i) => (
              <div key={i} data-testid={`reply-${i}`} className="rounded-xl border border-white/10 bg-[#131a29] p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs font-bold text-amber-300">{r.when}</p>
                  <CopyButton testId={`copy-reply-${i}`} text={r.say} />
                </div>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{r.say}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5 text-sm leading-relaxed text-slate-300">
            <p className="font-bold text-cyan-300">Golden rule of the comment section:</p>
            <p className="mt-1.5 text-slate-400">Reply to every comment within the first hour — TikTok pushes videos with active comment threads. Every "link?" reply is a free second ad for the next hundred viewers who read the comments before buying.</p>
          </div>
        </Section>

        <Section icon={Search} title="Google Ads Kit" sub="Search campaign assets matched to your page's SEO" testId="launch-kit-google-ads">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">High-intent keywords</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {GOOGLE_ADS.keywords.map((k) => (
              <span key={k} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300">{k}</span>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Responsive ad headlines</p>
            <CopyButton testId="copy-google-headlines" text={GOOGLE_ADS.headlines.join("\n")} />
          </div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {GOOGLE_ADS.headlines.map((h) => (
              <span key={h} className="rounded-xl border border-white/10 bg-[#131a29] px-4 py-2.5 text-sm text-slate-200">{h}</span>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Responsive ad descriptions (90 chars max)</p>
            <CopyButton testId="copy-google-descriptions" text={GOOGLE_ADS.descriptions.join("\n")} />
          </div>
          <div className="mt-2 grid gap-2">
            {GOOGLE_ADS.descriptions.map((d) => (
              <span key={d} className="rounded-xl border border-white/10 bg-[#131a29] px-4 py-2.5 text-sm text-slate-200">{d}</span>
            ))}
          </div>
          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sitelinks (6)</p>
            <CopyButton testId="copy-google-sitelinks" text={GOOGLE_ADS.sitelinks.map((s) => `${s.text}\n${s.desc1}\n${s.desc2}\n${s.url}`).join("\n\n")} />
          </div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            {GOOGLE_ADS.sitelinks.map((s, i) => (
              <div key={s.text} data-testid={`sitelink-${i}`} className="rounded-xl border border-white/10 bg-[#131a29] p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-bold text-cyan-300">{s.text}</p>
                  <CopyButton testId={`copy-sitelink-${i}`} text={`${s.text}\n${s.desc1}\n${s.desc2}\n${s.url}`} />
                </div>
                <p className="mt-1.5 text-xs text-slate-400">{s.desc1}</p>
                <p className="text-xs text-slate-400">{s.desc2}</p>
                <p className="mt-1.5 truncate font-mono text-[11px] text-slate-500">{s.url}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Clapperboard} title="Ad Creatives — Ready to Download" sub="Image ads, cover art and logo for your Meta & Google campaigns — click to download" testId="launch-kit-creatives">
          <div className="space-y-6">
            {CREATIVE_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{group.label}</p>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {group.items.map((item) => (
                    <div key={item.href} data-testid={`creative-card-${item.href.split("/").pop()}`} className="overflow-hidden rounded-2xl border border-white/10 bg-[#131a29]">
                      <div className="flex h-32 items-center justify-center bg-black/50">
                        {item.video ? (
                          <video src={item.href} poster={item.poster || item.href.replace(".mp4", ".jpg")} muted playsInline preload="metadata" className="h-full w-full object-contain" />
                        ) : (
                          <img src={item.href} alt={item.name} loading="lazy" className="h-full w-full object-contain" />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 px-4 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-white">{item.name}</p>
                          <p className="truncate text-[11px] text-slate-500">{item.spec}</p>
                        </div>
                        <a data-testid={`creative-download-${item.href.split("/").pop()}`} href={item.href} download className="flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-400 px-3.5 py-1.5 text-xs font-bold text-[#07090e] transition-transform hover:scale-105 active:scale-95">
                          <Download className="h-3 w-3" /> Get
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-5 text-sm leading-relaxed text-slate-300">
            <p className="font-bold text-cyan-300">How to use them:</p>
            <p className="mt-1.5 text-slate-400">TikTok/Reels: post the vertical videos natively with a trending sound — "Racing Mind" for cold audiences, "3AM Club" for late-night scrollers, "Social Proof" for retargeting. IG Feed: the square video + image ads. Stories: the 9:16 images with a link sticker to astraneb.com. Run 2-3 hooks against each other; kill the loser after $15-20 of spend.</p>
          </div>
        </Section>

        <Section icon={Eye} title="Live Orders" sub="Every order, delivery status, and refund controls" testId="launch-kit-orders">
          <button data-testid="load-orders-button" onClick={loadOrders} className="rounded-full border border-white/20 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-cyan-400/60 hover:text-cyan-300">
            Load orders (uses admin key)
          </button>
          {orders.length > 0 && (
            <div className="mt-4 space-y-2">
              {orders.map((o) => (
                <div key={o.order_number} data-testid={`order-row-${o.order_number}`} className="rounded-xl border border-white/10 bg-[#131a29] px-4 py-3 text-sm">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-mono font-bold text-cyan-300">{o.order_number}</span>
                    <span className="text-slate-200">{o.name}</span>
                    <span className="text-slate-500">{o.email}</span>
                    <span className="ml-auto font-bold text-white">${o.total.toFixed(2)}</span>
                    {o.payment_status === "paid" && <span className="rounded-full border border-emerald-400/40 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-emerald-300">Paid</span>}
                    {o.payment_status === "refunded" && <span className="rounded-full border border-rose-400/40 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-rose-300">Refunded</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span data-testid={`order-status-${o.order_number}`} className={`rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${STATUS_STYLES[o.fulfillment_status] || STATUS_STYLES.PENDING}`}>
                      {o.fulfillment_status || "PENDING"}
                    </span>
                    {o.tracking_number && <span className="font-mono text-xs text-slate-400">TRK: {o.tracking_number}</span>}
                    {o.fulfillment_note && <span className="text-xs text-slate-500">{o.fulfillment_note}</span>}
                    <span className="ml-auto flex items-center gap-3">
                      {o.payment_status === "paid" && (
                        <button data-testid={`refund-order-${o.order_number}`} onClick={() => refundOrder(o.order_number)} className="text-xs font-semibold text-rose-400 transition-colors hover:text-rose-300">
                          Refund
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </main>
    </div>
  );
}
